import styled from '@emotion/styled';
import { Box, Button, Grid, Tooltip, Typography, tooltipClasses } from '@mui/material';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { ReactComponent as PulsePlusIcon } from '../../assets/pulse-plus-icon.svg';
import { getUserProfile } from '../../modules/user/selectors';
import { Paths } from '../../routes';
import ImportURLField from './ImportURLField';

const DisableFeaturesTooltip = styled(({ className, maxWidth, height, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme, maxWidth, height }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#FCFCFC',
    fontSize: '12px',
    color: theme.palette.text.primary,
    borderRadius: '24px',
    border: '1px solid #E0E0E0',
    height: height || '140px',
    minWidth: '340px',
  },
}));

const ExploreButton = styled(Button)(({ theme }) => ({
  height: '28px',
  width: '105px',
  borderRadius: '27px',
  border: '1px solid #082739',
  color: 'rgba(9, 41, 60, 1)',
}));

export default function ImportURLList({
  urls,
  onChange,
  decorators,
  errorMessage,
  showAddButton = true,
  enableAddButton = true,
  disableFeatures,
  freeTrialExpired,
  subscriptionExpired,
  reachedUsageLimit,
  hasGlowingInputField,
}) {
  const [selectedUrlIndex, setSelectedUrlIndex] = useState(0);

  const history = useHistory();

  const { track } = useAnalytics();
  const user = useSelector(getUserProfile);

  const handleURLChanged = useCallback(
    (newUrl, replaceIndex) =>
      onChange(
        urls.map((urlObj, index) => (replaceIndex === index ? { url: newUrl, hasError: false } : urlObj))
      ),
    [onChange, urls]
  );

  const handleAddURL = useCallback(() => {
    onChange([...urls].concat([{ url: '', hasError: false }]));
    setSelectedUrlIndex(urls.length);
  }, [onChange, urls]);

  const handleDeleteURL = useCallback(
    (index) => {
      onChange(urls.slice(0, index).concat(urls.slice(index + 1)));
      setSelectedUrlIndex(urls.length - 2);
    },
    [onChange, urls]
  );

  const handleSelectedURL = useCallback((index) => {
    setSelectedUrlIndex(index);
  }, []);

  const redirectToSubscriptions = useCallback(() => {
    track(TrackEvent.CLICKED_BUTTON_TO_EXPLORE_PLANS_FROM_BLOCKED_FEATURE);
    history.push(`${Paths.automatedInsights.basePath}/subscription`);
  }, [history, track]);

  const getTooltipContent = useMemo(() => {
    if (disableFeatures) {
      if (freeTrialExpired) {
        return (
          <Box sx={{ marginTop: -1 }}>
            <Box mb={2} sx={{ marginLeft: 1, marginTop: 2 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                <b>Your free trial has ended.</b>
              </Typography>
            </Box>
            <Box sx={{ marginLeft: 1, marginTop: -1 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                Ready to keep the momentum going? Subscribe now to unlock full access.
              </Typography>
            </Box>
            <Box sx={{ marginRight: 1, marginTop: 2, textAlign: 'end' }}>
              <ExploreButton onClick={redirectToSubscriptions}>
                <Typography sx={{ textTransform: 'none', fontSize: '12px' }}>
                  <b>Explore plans</b>
                </Typography>
              </ExploreButton>
            </Box>
          </Box>
        );
      } else if (subscriptionExpired) {
        return (
          <Box sx={{ marginTop: -1 }}>
            <Box mb={2} sx={{ marginLeft: 1, marginTop: 2 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                <b>Subscription Expired</b>
              </Typography>
            </Box>
            <Box sx={{ marginLeft: 1, marginTop: -1 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                {user?.isBillingAdmin
                  ? `Your subscription to Pulse has expired. Upgrade to a full subscription today and continue
                transforming your digital assets with the power of WEVO Pulse.`
                  : `Your subscription to Pulse has expired. Please contact your administrator to upgrade to 
                  a full subscription and continue transforming your digital assets with the power of WEVO Pulse.`}
              </Typography>
            </Box>
            {user?.isBillingAdmin && (
              <Box sx={{ marginRight: 1, textAlign: 'end' }}>
                <ExploreButton onClick={redirectToSubscriptions}>
                  <Typography sx={{ textTransform: 'none', fontSize: '12px' }}>
                    <b>Explore plans</b>
                  </Typography>
                </ExploreButton>
              </Box>
            )}
          </Box>
        );
      } else if (reachedUsageLimit) {
        return (
          <Box sx={{ marginTop: -1 }}>
            <Box mb={2} sx={{ marginLeft: 1, marginTop: 2 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                <b>Usage Limit Reached!</b>
              </Typography>
            </Box>
            <Box sx={{ marginLeft: 1, marginTop: -1 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                You’ve reached your usage limit for the current plan. Upgrade to unlock additional capacity.
              </Typography>
            </Box>
            <Box sx={{ marginRight: 1, marginTop: 2, textAlign: 'end' }}>
              <ExploreButton onClick={redirectToSubscriptions}>
                <Typography sx={{ textTransform: 'none', fontSize: '12px' }}>
                  <b>Explore plans</b>
                </Typography>
              </ExploreButton>
            </Box>
          </Box>
        );
      }
      return '';
    }
    return '';
  }, [
    disableFeatures,
    freeTrialExpired,
    reachedUsageLimit,
    redirectToSubscriptions,
    subscriptionExpired,
    user?.isBillingAdmin,
  ]);

  const getDisableFeaturesTooltipHeight = () => {
    if (subscriptionExpired) {
      return user?.isBillingAdmin ? '160px' : '130px';
    }

    return '';
  };

  return (
    <Box>
      <Grid container alignItems="center" rowGap={2}>
        {urls.map((urlObj, index) => {
          return (
            <Fragment key={index}>
              <Grid item xs={12} md={2} sx={{ textAlign: { md: 'right' }, pr: 4 }}>
                {urls.length > 1 && (
                  <Typography color="primary" variant="body2">
                    Page {index + 1}
                  </Typography>
                )}
              </Grid>
              <Grid item xs md={8}>
                <ImportURLField
                  initialURL={urlObj?.url}
                  isFocused={index === selectedUrlIndex}
                  onStateChange={(importURL) => handleURLChanged(importURL, index)}
                  onRemove={urls.length > 1 ? () => handleDeleteURL(index) : null}
                  onFocus={() => handleSelectedURL(index)}
                  showErrors={false} // disable per-line errors because they throw off the formatting
                  notifyErrorStates={true} // this makes it so that onStateChange emits all state changes, even invalid urls
                  hasGlow={hasGlowingInputField}
                  disableFeatures={disableFeatures}
                  freeTrialExpired={freeTrialExpired}
                  subscriptionExpired={subscriptionExpired}
                  reachedUsageLimit={reachedUsageLimit}
                  user={user}
                />
              </Grid>
              <Grid
                item
                xs={1}
                md={2}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  pl: { xs: 1, sm: 3 },
                }}>
                {decorators?.[index] ? decorators[index] : <></>}
              </Grid>
            </Fragment>
          );
        })}
      </Grid>
      <Box mt={2}>
        <Grid container>
          <Grid item xs={12} md={2}></Grid>
          <Grid item xs md={8}>
            {disableFeatures ? (
              <DisableFeaturesTooltip title={getTooltipContent} height={getDisableFeaturesTooltipHeight()}>
                <Box
                  onClick={undefined}
                  sx={{
                    display: 'inline-flex',
                    cursor: disableFeatures ? 'not-allowed' : 'pointer',
                    opacity: enableAddButton ? 1.0 : 0.7,
                    visibility: showAddButton ? 'initial' : 'hidden',
                  }}>
                  <PulsePlusIcon style={{ height: '16px', width: '16px' }} />
                  <Typography color="primary" fontSize={12} ml={1}>
                    Add
                  </Typography>
                </Box>
              </DisableFeaturesTooltip>
            ) : (
              <Tooltip title={enableAddButton ? '' : 'Reached max number of URLs'} placement="right">
                <Box
                  onClick={enableAddButton ? () => handleAddURL() : undefined}
                  sx={{
                    display: 'inline-flex',
                    cursor: disableFeatures ? 'not-allowed' : 'pointer',
                    opacity: enableAddButton ? 1.0 : 0.7,
                    visibility: showAddButton ? 'initial' : 'hidden',
                  }}>
                  <PulsePlusIcon style={{ height: '16px', width: '16px' }} />
                  <Typography color="primary" fontSize={12} ml={1}>
                    Add
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box mt={1}>
        <Grid container>
          <Grid item xs={12} md={2} />
          <Grid item xs md={8}>
            <Typography color="error" fontSize={12}>
              {urls?.length && errorMessage}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
